// deploy: 2026-05-26
import { createRoot } from 'react-dom/client'
import { installGlobalErrorReporting } from './utils/clientErrors';
import './styles/theme.css'   // global colour tokens — must load before component CSS
import './index.css'
import App from './App.jsx'
import { installGlobalDialogs } from './utils/dialogs'
import { isMsalConfigured, getMsalInstance } from './utils/msalConfig'
import { startPerfMonitor } from './utils/perfMonitor'
import { initTheme } from './utils/theme'
import { installFileAuthInterceptor } from './utils/installFileAuth'
import { applyUiZoom } from './utils/uiZoom'
import { initToolbarIconNormalizer } from './utils/normalizeToolbarIcons'
import { initDocPropSaveErrorToast } from './utils/docPropSaveErrorToast'

// If this window is an MSAL auth popup returning from Microsoft login,
// do NOT boot the app. The parent window polls this popup's URL to extract
// the auth code from the hash; booting React here would consume/clear the
// hash (via handleRedirectPromise) before the parent can read it, leaving
// the popup stuck on the app page instead of closing.
const isMsalAuthPopup =
  window.opener &&
  window.opener !== window &&
  /[#&](code|error|state)=/.test(window.location.hash);

if (isMsalAuthPopup) {
  // Intentionally render nothing and do nothing. The parent's MSAL instance
  // will detect the hash and close this popup.
} else {

// Apply the persisted colour theme before first paint to avoid a flash.
initTheme()

// Apply the persisted interface zoom before first paint (same reason).
applyUiZoom()
// The symbol STYLE (thin / firm / duo / solid, Settings > Annotations toolbar)
// is a data attribute on <html> that theme.css reads. MainView sets it when
// it mounts; set it at boot too, so every page that wears MainView's rail and
// header (Objects, Properties) draws its symbols the same way from the start.
try {
  const symbols = localStorage.getItem('annotationSymbolStyle')
  if (symbols && symbols !== 'thin') document.documentElement.setAttribute('data-symbols', symbols)
} catch { /* storage unavailable */ }

// Keep the blue-glow washes off everywhere. The home/shell now follows the
// app theme (the fixed palette lock is retired) so themes apply consistently
// across every page. Done before first paint so there's no flash.
document.documentElement.classList.add('pidly-no-blue-tint')

installGlobalDialogs()
// Add the auth token to backend file/PDF fetches (PDF blobs, save-markups,
// upload, etc.) so those routes can be gated with requireAuth. Must run
// before any file fetch fires.
installFileAuthInterceptor()
// Perf instrumentation — logs heap / DOM / listener counts every
// 30s plus named breadcrumbs at key actions. Filter the DevTools
// console by `[PERF]` to see the timeline. Exposes window.pidlyPerf
// for ad-hoc inspection (pidlyPerf.snap(), pidlyPerf.listeners()).
startPerfMonitor()

async function boot() {
  if (isMsalConfigured) {
    try {
      const instance = getMsalInstance();
      await instance.initialize();

      // Handle the auth redirect response (if returning from Microsoft login).
      // This processes the code in the URL hash and exchanges it for tokens.
      const response = await instance.handleRedirectPromise();
      if (response) {
        // Successfully authenticated via redirect — mark as logged in
        localStorage.setItem('authenticated', 'true');
        localStorage.setItem('msalAccount', JSON.stringify({
          name: response.account.name,
          username: response.account.username,
        }));
      }
    } catch (e) {
      console.warn('MSAL init:', e);
    }
  }

// One boot line answering "which build is this?" — deploy staleness kept
// masquerading as "the fix didn't work".
console.log('[pidly] build', typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev');
// ...and which BACKEND is live (its git commit, via /api/version): the
// console's first two lines now say exactly what is deployed on both
// sides, which ends the "did the fix actually ship" guessing game.
try {
  import('./utils/config.js').then(({ BACKEND_URL }) =>
    fetch(`${BACKEND_URL}/api/version`).then(r => r.json()).then(d =>
      console.log('[pidly] backend', (d.commit || 'unknown').slice(0, 9), 'started', d.startedAt || '?'),
    ).catch(() => {}),
  ).catch(() => {});
} catch { /* never blocks startup */ }
installGlobalErrorReporting();

  createRoot(document.getElementById('root')).render(
    <App />,
  );

  // Give every toolbar/rail icon a uniform drawn size regardless of its source
  // viewBox / artwork tightness (see the util). Safe to call once globally — it
  // only touches the toolbar/rail icon svgs and re-applies on re-render.
  initToolbarIconNormalizer();

  // Surface doc-property save failures (both surfaces dispatch to it) so a
  // failed granular save is visible, not silent.
  initDocPropSaveErrorToast();
}

boot();

}
